import axiosInstance from '../lib/api/axiosInstance';
import {
  Task,
  TaskComment,
  EmailTaskAssociation
} from '../types';

// Récupérer une tâche par son ID
export const getTaskById = async (taskId: string | number): Promise<Task> => {
  try {
    const response = await axiosInstance.get<Task>(`/tasks/${taskId}`);
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la récupération de la tâche ${taskId}:`, error);
    throw error;
  }
};

// Récupérer les emails associés à une tâche
export const getTaskEmails = async (taskId: string | number): Promise<EmailTaskAssociation[]> => {
  try {
    const response = await axiosInstance.get<{ emails: EmailTaskAssociation[] }>(`/tasks/${taskId}/emails`);
    return response.data.emails || [];
  } catch (error) {
    console.error(`Erreur lors de la récupération des emails pour la tâche ${taskId}:`, error);
    throw error;
  }
};

// Mettre à jour une tâche
export const updateTask = async (taskId: number, data: Partial<Task>): Promise<Task> => {
  try {
    console.log(`Attempting to update task ${taskId} with data:`, data);
    
    // Vérification des types
    console.log("Data types:", {
      taskId: typeof taskId,
      taskIdValue: taskId,
      dataType: typeof data,
      dataKeys: Object.keys(data)
    });
    
    // Si on essaie de mettre à jour assignedToId, formatons correctement la requête
    if ('assignedToId' in data) {
      const assignedToId = data.assignedToId;
      // Créer une nouvelle copie pour ne pas modifier l'objet original
      const formattedData = { ...data };
      
      console.log("Formatting assignedToId update:", {
        originalValue: assignedToId,
        formattedValue: assignedToId === null ? null : assignedToId
      });
      
      // Assurez-vous que la valeur assignedToId est correctement formatée
      // notamment pour les valeurs null (pour désassigner)
      formattedData.assignedToId = assignedToId === null ? null : assignedToId;
      
      const response = await axiosInstance.put<Task>(`/tasks/${taskId}`, formattedData);
      console.log(`Task ${taskId} updated successfully, server response:`, response.data);
      return response.data;
    } else {
      // Requête normale pour les autres champs
      const response = await axiosInstance.put<Task>(`/tasks/${taskId}`, data);
      console.log(`Task ${taskId} updated successfully, server response:`, response.data);
      return response.data;
    }
  } catch (error: any) {
    console.error(`Error updating task ${taskId}:`, error);
    // Log plus détaillé de l'erreur
    if (error.response) {
      console.error('Response error data:', error.response.data);
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request made but no response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    console.error('Error config:', error.config);
    
    throw error;
  }
};

// Récupérer les commentaires d'une tâche
export const getTaskComments = async (taskId: string | number): Promise<TaskComment[]> => {
  try {
    const response = await axiosInstance.get<TaskComment[]>(`/tasks/${taskId}/comments`);
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la récupération des commentaires de la tâche ${taskId}:`, error);
    throw error;
  }
};

// Ajouter un commentaire à une tâche
export const addTaskComment = async (taskId: string | number, content: string): Promise<TaskComment> => {
  try {
    const response = await axiosInstance.post<TaskComment>(`/tasks/${taskId}/comments`, { content });
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de l'ajout d'un commentaire à la tâche ${taskId}:`, error);
    throw error;
  }
};

// Supprimer un commentaire
export const deleteTaskComment = async (commentId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/tasks/comments/${commentId}`);
  } catch (error) {
    console.error(`Erreur lors de la suppression du commentaire ${commentId}:`, error);
    throw error;
  }
};
